import React from "react";
import { Phone } from "lucide-react";
import { V } from "@/lib/vocab";

// Voice calls are saved to the conversation thread as ONE message whose body is a flat
// block: "📞 AI phone call — 52s\n• <logged action>\n— Transcript —\nAI: ...\nCaller: ...".
// This parses that block into a header + alternating turns so the history can render it as a
// real back-and-forth (AI vs resident) instead of one indistinct paragraph.
function parseCall(body) {
  const lines = (body || "").split("\n");
  const header = [];
  const turns = [];
  let inTranscript = false;
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (line.startsWith("— Transcript —")) { inTranscript = true; continue; }
    if (!inTranscript) { if (line.trim()) header.push(line); continue; }
    const m = line.match(/^(AI|Caller):\s?(.*)$/);
    if (m) turns.push({ speaker: m[1], text: m[2] });
    else if (turns.length && line.trim()) turns[turns.length - 1].text += "\n" + line;
  }
  return { header, turns };
}

export function VoiceCallTranscript({ body, createdAt, fmtTime }) {
  const { header, turns } = parseCall(body);
  const title = (header[0] || "AI phone call").replace(/^📞\s*/, "");
  const summary = header.slice(1);
  return (
    <div data-testid="voice-call-transcript" className="rounded-2xl border border-sand-200 bg-white/70 p-3">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-ink/70">
        <Phone className="h-3.5 w-3.5 text-redline-600 shrink-0" /> {title}
      </div>
      {summary.length > 0 && (
        <div className="mt-1 space-y-0.5 text-[11px] text-ink/60">
          {summary.map((s, i) => <div key={i}>{s}</div>)}
        </div>
      )}
      {turns.length > 0 ? (
        <div className="mt-2 space-y-1.5" data-testid="voice-call-turns">
          {turns.map((t, i) => {
            const isAI = t.speaker === "AI";
            return (
              <div key={i} className={`flex ${isAI ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-1.5 text-[13px] whitespace-pre-wrap ${isAI
                  ? "bg-ink text-white rounded-2xl rounded-tr-md"
                  : "bg-sand-100 text-ink border border-sand-200 rounded-2xl rounded-tl-md"}`}>
                  <span className={`block text-[9px] uppercase tracking-wide mb-0.5 ${isAI ? "text-white/60" : "text-ink/45"}`}>
                    {isAI ? "AI" : `${V.one("person")}`}
                  </span>
                  {t.text}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="mt-1 text-[11px] italic text-ink/40">Transcript not stored for this call.</div>
      )}
      {createdAt && fmtTime && <div className="mt-1.5 text-[10px] text-muted-foreground text-right">{fmtTime(createdAt)}</div>}
    </div>
  );
}
